import moment from 'moment';
import { DATE_FORMAT_PICKER } from 'src/config';
import * as Yup from 'yup';
import _ from 'lodash';

export const convertDateToISO = (dateInput: unknown): string | null => {
  if (!dateInput) return null;

  const dateString = String(dateInput).trim();
  if (!dateString) return null;

  let date = moment(dateString, DATE_FORMAT_PICKER, true);
  if (date.isValid()) {
    return date.toISOString();
  }

  const alternativeFormats = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD.MM.YYYY', 'DD MM YYYY'];

  for (const format of alternativeFormats) {
    date = moment(dateString, format, true);
    if (date.isValid()) {
      return date.toISOString();
    }
  }

  return null;
};

export const convertToNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  if (_.isNumber(value)) return _.isNaN(value) ? null : value;
  if (_.isString(value) && value.trim() === '') return null;
  const num = parseFloat(String(value));
  return _.isNaN(num) ? null : num;
};

export const isValidNumberValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (_.isString(value) && value.trim() === '') return true;
  if (_.isNumber(value)) return !_.isNaN(value);
  const strValue = String(value).trim();
  const num = Number(strValue);
  return !_.isNaN(num) && _.isFinite(num);
};

export const convertToString = (value: string | null | undefined | number): string | null => {
  if (!value || String(value).trim() === '') return null;
  return String(value).trim();
};

export const convertToMaturityLevel = (value: string | number | null | undefined): string | null => {
  if (!value) return null;

  const stringValue = String(value).trim().toUpperCase();

  // If it's already in LEVEL_X format, return as is
  if (stringValue.match(/^LEVEL_[0-5]$/)) {
    return stringValue;
  }

  // If it's a numeric value (0-5), convert to LEVEL_X format
  if (stringValue.match(/^[0-5]$/)) {
    return `LEVEL_${stringValue}`;
  }

  // If it's just the number part (like "1" from "LEVEL_1"), convert to LEVEL_X format
  const numericMatch = stringValue.match(/^(\d)$/);
  if (numericMatch && ['0', '1', '2', '3', '4', '5'].includes(numericMatch[1])) {
    return `LEVEL_${numericMatch[1]}`;
  }

  return null;
};

export interface ValidationErrorItem {
  rowIndex: number;
  errors: {
    path: string;
    message: string;
  }[];
}

export interface ParseResult<T> {
  data: T[];
  errors: unknown[];
  meta: {
    delimiter: string;
    linebreak: string;
    aborted: boolean;
    truncated: boolean;
    cursor: number;
  };
}

type ValidateRow = (params: {
  row: any;
  rowIndex: number;
  schema: Yup.ObjectSchema<any>;
  pathNames: Record<string, string>;
}) => Promise<{
  rowIndex: number;
  errors: {
    path: string | undefined;
    message: string;
  }[];
} | null>;

export const validateRow: ValidateRow = async ({ row, rowIndex, schema, pathNames }) => {
  try {
    await schema.validate(row, { abortEarly: false });
    return null;
  } catch (err) {
    if (err instanceof Yup.ValidationError) {
      const customErrors = err.inner.map((e) => {
        return { path: _.get(pathNames, e.path as string, e.path), message: e.message };
      });
      return { rowIndex, errors: customErrors };
    } else {
      return {
        rowIndex,
        errors: [{ path: 'Unexpected Error', message: 'An unexpected error occurred during validation.' }],
      };
    }
  }
};

export const delimiters = [',', ';', '\t', '|', ':', '^', '~'];
